import { Box, Button, Stack } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useFileLoader } from '../../../../../hooks/useFileLoader'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import {
  deleteImage,
  finishInstallation,
  getImages,
} from '../../../../../services/ApiService'
import {
  closeModalFn,
  openModalFn,
} from '../../../../../utils/controllers/ModalController'
import { getComp } from '../../../../../utils/utils/DI'
import { checkUploadedImages } from '../../../../SingleRequestPage/components/SingleRequestDrawer/content/DrawerContentInstallation'
import { RequestType } from '../../../../SingleRequestPage/types/Request.type'
import { REPORT_KEYS } from '../../../../SingleRequestReport/consts/request-report.keys'
import { newRequestDrawerStore } from '../../../stores/NewRequestDrawerStore'
import InstallationDataAcc from '../../InstalationRequestDrawer/components/InstallationDataAcc'
import { useInstallationDataNew } from '../../InstalationRequestDrawer/hooks/useInstallationDataNew'

interface InstallationDataAndFinishBtnProps {
  request: RequestType
}

const InstallationDataAndFinishBtn: React.FC<
  InstallationDataAndFinishBtnProps
> = ({ request }) => {
  const { files, handleSetFiles, deleteFile, filesError } =
    useFileLoader('install')
  const { form, isValid, watch, modelId, isEquipmentIdChecked } =
    useInstallationDataNew(request)

  const invalidate = useInvalidateQueries()

  const { data: images } = useQuery({
    queryKey: ['get-request-images', request.id],
    queryFn: () => getImages(request.id),
  })

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['finish-installation'],
    mutationFn: () =>
      finishInstallation(getBody(request, watch, modelId, files)),
    onSuccess: (data) => {
      const result = checkUploadedImages(data)
      if (result) {
        newRequestDrawerStore.updateRequest(data)
        invalidate([
          'requests-table',
          'get-request-images',
          REPORT_KEYS.GET_REPORT_TABLE,
        ])
        form.setIsEditMode(false)
      }
    },
  })

  const { mutateAsync: deleteImg } = useMutation({
    mutationKey: ['delete-request-image'],
    mutationFn: (id: number) => deleteImage({ imageId: id }),
    onSuccess: () => {
      closeModalFn['progress-backdrop']?.()
      invalidate(['get-request-images'])
    },
    onError() {
      closeModalFn['progress-backdrop']?.()
    },
  })

  useEffect(() => {
    if (images) newRequestDrawerStore.getImages(images)
  }, [images])

  const onDelete = (id: number) => {
    openModalFn['transaction-confirmation']?.({
      text1: `Подтвердить удаление фотографии из заявки?`,

      action: () => deleteImg(id),
      processText: `Выполняется удаление фотографии из заявки`,
    })
  }

  return (
    <Stack spacing={'16px'}>
      <InstallationDataAcc
        request={request}
        store={null}
        photoPermission={
          getComp('PhotoSettingsStore')._photoSettings['install']
        }
        files={files ?? []}
        handleSetFiles={handleSetFiles}
        deleteFile={deleteFile}
        form={form}
        isValid={isValid}
        deleteImage={onDelete}
        watch={watch}
        isEquipmentIdChecked={isEquipmentIdChecked}
      />
      {request.status === 5 && (
        <Box pb={'8px'} pr={'32px'}>
          <Button
            variant='contained'
            onClick={() => mutateAsync()}
            loading={isPending}
            disabled={!isValid || filesError}
            sx={{ margin: '0 16px!important ' }}
            fullWidth
          >
            Завершить установку
          </Button>
        </Box>
      )}
    </Stack>
  )
}

const getBody = (
  request: RequestType,
  watch: any,
  modelId: string,
  files: any,
) => ({
  orderId: request.id,
  date: moment(
    moment(watch('installationDate')).format('DD-MM-YYYY'),
    'DD-MM-YYYY',
  )
    .local(true)
    .format(),
  receiverSerialNumber: watch('receiverSerialNumber'),
  receiverModelId: modelId,
  ...files?.map((el: any) => el.file),
})

export default observer(InstallationDataAndFinishBtn)
